import { Navigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { ReactNode } from "react";
import { jwtDecode } from "jwt-decode";

type AdminProtectedRouteProps = {
  children: ReactNode;
};

type DecodedToken = {
  id: string;
  email: string;
  role: "ADMIN" | "CLIENT" | "FACILITATOR" | "SUB_ADMIN" | "ECOSYSTEM_HEAD";
  exp: number;
  iat: number;
};

export function AdminProtectedRoute({ children }: AdminProtectedRouteProps) {
  const location = useLocation();
  const adminToken = Cookies.get("token");

  if (!adminToken) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  try {
    const decoded = jwtDecode<DecodedToken>(adminToken);

    const currentTime = Date.now() / 1000;
    if (decoded.exp < currentTime) {
      Cookies.remove("token");
      return <Navigate to="/admin/login" state={{ from: location }} replace />;
    }

    // Client trying to access admin routes — redirect to client dashboard
    if (decoded.role === "CLIENT") {
      return <Navigate to="/profile" replace />;
    }

    if (decoded.role === "FACILITATOR") {
      return <Navigate to="/facilitator/dashboard" replace />;
    }

    if (decoded.role === "ECOSYSTEM_HEAD") {
      return <Navigate to="/ecosystem-head/dashboard" replace />;
    }

    // ADMIN (full) and SUB_ADMIN (read-only) may both access the admin panel.
    if (decoded.role !== "ADMIN" && decoded.role !== "SUB_ADMIN") {
      Cookies.remove("token");
      return <Navigate to="/admin/login" state={{ from: location }} replace />;
    }

    return <>{children}</>;
  } catch (error) {
    Cookies.remove("token");
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }
}
